import { useContext, useEffect, useState } from 'react';

import axios from 'axios';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import MixPanelHelper from 'helpers/mixpanel';
import { node, oneOfType, shape, string } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Button } from '@olist/design-system';
import { ChevronDownOutline } from '@olist/design-system-icons';
import {
  Alert,
  Box,
  Checkbox,
  Flex,
  Icon,
  Loading,
  Text,
  Button as UnitedButton,
} from '@olist/united';

import { API_URLS, MIXPANEL_EVENTS } from '../../constants';
import { RootContext } from '../leads-provider';
import RequisitesQuestionsModal from '../requisites-questions-modal/requisites-questions-modal';
import { BRAZIL_STATES } from './constants';
import {
  CheckboxGroup,
  CheckboxWrapper,
  CustomLabel,
  CustomSelect,
  RequiredField,
} from './requisites-form.style';

const { VIEWED_REQUIREMENTS_MODAL } = MIXPANEL_EVENTS;

const formInputNames = {
  hasPjAccount: 'hasPjAccount',
  hasIssueXml: 'hasIssueXml',
  hasPackageDimensions: 'hasPackageDimensions',
  state: 'state',
};

const CheckboxLabel = ({ children }) => (
  <Box pl={2}>
    <b>{children}</b>
  </Box>
);

CheckboxLabel.propTypes = {
  children: node.isRequired,
};

const RequisitesForm = ({ initialRequisitesValues }) => {
  const { t } = useTranslation();

  const { appUrls, leadIdCookie, leadProfileCookie, setLeadRequisites, setLeadTotalFields } =
    useContext(RootContext);

  const [formValues, setFormValues] = useState({
    [formInputNames.hasPjAccount]: false,
    [formInputNames.hasIssueXml]: false,
    [formInputNames.hasPackageDimensions]: false,
    [formInputNames.state]: '',
  });
  const [stateError, setStateError] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showRequisitesQuestionsModal, setShowRequisitesQuestionsModal] = useState(false);

  const [postDataResult, setPostDataResult] = useState({
    errorPostRequisites: false,
    isLoadingPostRequisites: false,
    requisitesResult: null,
    successPostRequisites: false,
  });
  const { errorPostRequisites, isLoadingPostRequisites } = postDataResult;

  const submitRequisites = async () => {
    try {
      const dataToSend = {
        hasBankAccountAssociatedCnpj: formValues.hasPjAccount,
        hasNfeXml: formValues.hasIssueXml,
        respectCorreiosMaxDimensions: formValues.hasPackageDimensions,
        state: formValues.state,
      };

      setPostDataResult((d) => ({
        ...d,
        isLoadingPostRequisites: true,
      }));
      const response = await axios.post(
        API_URLS.getRequisites({ leadId: leadIdCookie }),
        dataToSend
      );

      setLeadRequisites(
        JSON.stringify({
          tem_cpnj: dataToSend.hasBankAccountAssociatedCnpj,
          emite_nota_fiscal: dataToSend.hasNfeXml,
          respeita_dimensoes_correios: dataToSend.respectCorreiosMaxDimensions,
          estado: dataToSend.state,
        })
      );

      sendGTMEvent('etapa_cadastro', {
        etapa: 'requisitos',
      });

      const data = response?.data;

      setPostDataResult((d) => ({
        ...d,
        requisitesResult: data,
        isLoadingPostRequisites: false,
        successPostRequisites: true,
        errorPostRequisites: false,
      }));

      if (data) {
        const totalChecked = Object.keys(formValues).reduce((sum, key) => {
          return formValues[key] ? sum + 1 : sum;
        }, 0);

        setLeadTotalFields(totalChecked);

        const isQualified = data?.status && data?.status !== 'unqualified';

        if (isQualified) {
          window.location.href = data.redirectTo || appUrls.CATEGORIES_URL;
          return;
        }

        window.location.href = appUrls.UNQUALIFIED_URL;
        return;
      }
    } catch {
      setPostDataResult((d) => ({ ...d, successPostRequisites: false, errorPostRequisites: true }));
    }
  };

  useEffect(() => {
    if (Object.keys(initialRequisitesValues).length) {
      setFormValues((values) => ({
        ...values,
        [formInputNames.hasPjAccount]: initialRequisitesValues.hasBankAccountAssociatedCnpj,
        [formInputNames.hasIssueXml]: initialRequisitesValues.hasNfeXml,
        [formInputNames.hasPackageDimensions]: initialRequisitesValues.respectCorreiosMaxDimensions,
        [formInputNames.state]: initialRequisitesValues.state,
      }));
    }
  }, [initialRequisitesValues]);

  useEffect(() => {
    const isAllFieldsUnchecked = Object.keys(formValues).every((key) => !formValues[key]);
    setIsSubmitButtonDisabled(isLoadingPostRequisites || isAllFieldsUnchecked);
  }, [formValues, isLoadingPostRequisites]);

  useEffect(() => {
    if (errorPostRequisites && !isLoadingPostRequisites) {
      setShowErrorAlert(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorPostRequisites, isLoadingPostRequisites]);

  const handleChangeCheckbox = ({ name, value }) => {
    setFormValues((values) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formValues.state) {
      setStateError(true);
    } else {
      submitRequisites();
    }
  };

  const handleOpenRequisitesQuestionsModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowRequisitesQuestionsModal(true);
    const userData = {
      id: leadIdCookie || leadProfileCookie?.id,
      ...leadProfileCookie,
    };
    const mixpanelInstance = MixPanelHelper(userData)?.getInstance();
    mixpanelInstance?.track(VIEWED_REQUIREMENTS_MODAL);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <CustomSelect>
          <CustomLabel>{t`logistic address district`}</CustomLabel>
          <select
            data-testid="requisites-state"
            required
            name="state"
            id="requisites-state"
            value={formValues?.state}
            onChange={(e) => {
              setStateError(false);
              setFormValues((values) => ({ ...values, [formInputNames.state]: e.target.value }));
            }}
          >
            <option value="" className="empty-option">{t`select district`}</option>
            {BRAZIL_STATES.map(({ name, slug }) => {
              return (
                <option key={slug} value={slug}>
                  {`${name} (${slug})`}
                </option>
              );
            })}
          </select>
          <ChevronDownOutline width={24} height={24} color="#696d8c" />
        </CustomSelect>
        {stateError && <RequiredField>{t`required field`}</RequiredField>}
        <CheckboxGroup>
          <Checkbox
            data-testid="hasPjAccount"
            name={formInputNames.hasPjAccount}
            label={
              <CheckboxLabel>{t`i have a pj bank account associated with the cnpj`}</CheckboxLabel>
            }
            onChange={({ target }) =>
              handleChangeCheckbox({ name: formInputNames.hasPjAccount, value: target.checked })
            }
            checked={formValues.hasPjAccount}
          />
        </CheckboxGroup>
        <CheckboxGroup>
          <Checkbox
            data-testid="hasIssueXml"
            name={formInputNames.hasIssueXml}
            label={<CheckboxLabel>{t`i issue electronic invoice (NF-e)`}</CheckboxLabel>}
            onChange={({ target }) =>
              handleChangeCheckbox({ name: formInputNames.hasIssueXml, value: target.checked })
            }
            checked={formValues.hasIssueXml}
          />
        </CheckboxGroup>
        <CheckboxGroup>
          <CheckboxWrapper>
            <Checkbox
              data-testid="hasPackageDimensions"
              name={formInputNames.hasPackageDimensions}
              label={<CheckboxLabel>{t`my packages are within the measures`}</CheckboxLabel>}
              onChange={({ target }) =>
                handleChangeCheckbox({
                  name: formInputNames.hasPackageDimensions,
                  value: target.checked,
                })
              }
              checked={formValues.hasPackageDimensions}
              style={{ flexDirectiopn: 'column' }}
            />
            <Text fontSize={1} color="foundation.shadeDark" ml="34px">
              {t`allowed below: 100 cm X 100cm X 100cm, 30 kg`}
            </Text>
          </CheckboxWrapper>
        </CheckboxGroup>

        <Box maxWidth="250px" mt={2}>
          <UnitedButton
            type="button"
            variation="link"
            size="small"
            onClick={handleOpenRequisitesQuestionsModal}
          >
            <Flex alignItems="center">
              <Icon name="question-circle-outline" color="foundation.linkPrimary" size="20px" />
              <Text ml={2} color="foundation.linkPrimary">
                {t`questions about requirements`}
              </Text>
            </Flex>
          </UnitedButton>
        </Box>
        <Box mt={4}>
          {showErrorAlert && (
            <Alert
              data-testid="form-error-alert"
              mb={3}
              messages={[t`ops, the form could not be submitted. Try again`]}
            />
          )}
          <Button type="submit" disabled={isSubmitButtonDisabled} variation="primary">
            {isLoadingPostRequisites ? <Loading variation="shadow" /> : t`next`}
          </Button>
        </Box>
      </form>
      <RequisitesQuestionsModal
        isOpen={showRequisitesQuestionsModal}
        onRequestClose={() => setShowRequisitesQuestionsModal(false)}
      />
    </>
  );
};

RequisitesForm.propTypes = {
  initialRequisitesValues: oneOfType([
    shape({}),
    shape({
      hasBankAccountAssociatedCnpj: string.isRequired,
      hasNfeXml: string.isRequired,
      respectCorreiosMaxDimensions: string.isRequired,
    }),
  ]).isRequired,
};

export default RequisitesForm;
